// Túi (trang con, UI_SPEC §18.1): 6×6 phân ngăn Trang bị · Đan dược · Nguyên liệu, lọc theo phẩm / hệ.
// Trang con là bề mặt duy nhất được cuộn.
import type { JSX } from 'preact';
import { useState } from 'preact/hooks';
import { t } from '@i18n/t';
import { formatFull } from '@i18n/format';
import { BALANCE } from '@data/balance';
import { baseDef } from '@data/items/bases';
import { goodsDef, parseStackKey } from '@data/items/goods';
import { RARITIES, type Rarity } from '@data/items/types';
import { ELEMENT_IDS, type Element } from '@data/elements';
import { betterInBag, inventory } from '@state/modules/inventory';
import { openWindow } from '@state/modules/ui';
import { wallet } from '@state/modules/wallet';
import { ItemSlot } from '../kit/ItemSlot';
import {
  elementClass,
  goodsArt,
  goodsIcon,
  goodsName,
  goodsTier,
  itemArt,
  itemIcon,
  itemName,
  rarityClass,
} from '../itemView';

type Pane = 'gear' | 'pill' | 'mat';

export function BagPage(): JSX.Element {
  const [pane, setPane] = useState<Pane>('gear');
  const [rarity, setRarity] = useState<Rarity | null>(null);
  const [element, setElement] = useState<Element | null>(null);
  const inv = inventory.value;
  const better = betterInBag.value.ratio;
  const gear = inv.bag
    .filter((i) => (!rarity || i.rarity === rarity) && (!element || baseDef(i.base).element === element))
    .sort((a, b) => RARITIES.indexOf(b.rarity) - RARITIES.indexOf(a.rarity) || b.giai - a.giai);
  const stacks = Object.entries(inv.goods)
    .filter(([k]) => {
      const kind = goodsDef(parseStackKey(k).id).kind;
      return pane === 'pill' ? kind === 'pill' : kind !== 'pill';
    })
    .filter(([k]) => !element || goodsDef(parseStackKey(k).id).element === element)
    .sort(([a], [b]) => goodsTier(b) - goodsTier(a) || a.localeCompare(b));
  const cells: JSX.Element[] =
    pane === 'gear'
      ? gear.map((it) => (
          <ItemSlot
            key={it.uid}
            id={`bag-${it.uid}`}
            size="md"
            q={rarityClass(it.rarity)}
            art={itemArt(it)}
            glyph={itemIcon(it)}
            el={elementClass(baseDef(it.base).element)}
            bl={String(it.giai)}
            br={it.enhance ? `+${it.enhance}` : undefined}
            label={t('ui.item.aria', {
              name: `${itemName(it)}${it.enhance ? ` +${it.enhance}` : ''}`,
              rarity: t(`item.rarity.${it.rarity}`),
              n: it.giai,
            })}
            signal={
              (better.get(it.uid) ?? 0) >= BALANCE.UPGRADE_MIN_LC_GAIN
                ? 'better'
                : inv.fresh.includes(it.uid)
                  ? 'new'
                  : null
            }
            onClick={() => openWindow({ kind: 'item', uid: it.uid })}
          />
        ))
      : stacks.map(([k, n]) => (
          <ItemSlot
            key={k}
            id={`bag-${k}`}
            size="md"
            q={rarityClass(goodsTier(k))}
            art={goodsArt(k)}
            glyph={goodsIcon(k)}
            el={elementClass(goodsDef(parseStackKey(k).id).element)}
            br={n > 1 ? formatFull(n) : undefined}
            label={goodsName(k)}
            onClick={() => openWindow({ kind: 'goods', key: k })}
          />
        ));
  const pad = Math.max(0, Math.ceil(Math.max(cells.length, BALANCE.BAG_CAPACITY) / 6) * 6 - cells.length);
  const w = wallet.value;
  return (
    <>
      <div class="ht-wintabs" role="tablist">
        {(['gear', 'pill', 'mat'] as Pane[]).map((p) => (
          <button
            type="button"
            key={p}
            role="tab"
            aria-selected={pane === p}
            class={pane === p ? 'on' : ''}
            data-id={`bag-tab-${p}`}
            onClick={() => setPane(p)}
          >
            {t(`ui.bag.${p}`)}
          </button>
        ))}
      </div>
      <div class="ht-grid bag-grid" data-id="bag-grid">
        {cells}
        {Array.from({ length: pad }, (_, i) => (
          <div class="ht-slot" data-state="empty" key={`e${i}`} />
        ))}
      </div>
      <div class="bag-filters">
        {pane === 'gear' &&
          RARITIES.slice(0, 6).map((r) => (
            <button
              type="button"
              key={r}
              class={`ht-chip q-chip ${rarityClass(r)}${rarity === r ? ' sel' : ''}`}
              aria-pressed={rarity === r}
              data-id={`bag-rarity-${r}`}
              onClick={() => setRarity(rarity === r ? null : r)}
            >
              {t(`item.rarity.${r}`)}
            </button>
          ))}
      </div>
      <div class="bag-filters">
        {ELEMENT_IDS.map((e) => (
          <button
            type="button"
            key={e}
            class={`ht-chip${element === e ? ' sel' : ''}`}
            aria-pressed={element === e}
            data-id={`bag-element-${e}`}
            onClick={() => setElement(element === e ? null : e)}
          >
            <i class={`it-tl ${elementClass(e)}`} />
            {t(`element.${e}`)}
          </button>
        ))}
      </div>
      <div class="bag-wallet">
        <span class="ht-chip">
          {t('currency.dust')} {formatFull(w.dust)}
        </span>
        <span class="ht-chip">
          {t('currency.refiningStone')} {formatFull(w.refiningStone)}
        </span>
        {ELEMENT_IDS.filter((e) => w[`spiritShard.${e}`] > 0).map((e) => (
          <span class="ht-chip" key={e}>
            {t(`currency.spiritShard.${e}`)} {formatFull(w[`spiritShard.${e}`])}
          </span>
        ))}
      </div>
    </>
  );
}
